'use client';

import { useMemo, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import dynamic from 'next/dynamic';
import CosmosDataProvider, { type CosmosDataWithTimestamps } from '@/CosmosDataProvider';
import { NativeAdapterDriverObserver } from '@/drivers/nativeAdapterDriver/NativeAdapterDriverObserver';
import { useAdapterRuntimeEffects } from '@/drivers/nativeAdapterDriver/useAdapterRuntimeEffects';
import { useAdapterRuntimeTicks } from '@/drivers/nativeAdapterDriver/useAdapterRuntimeTicks';
import { useSupplementalCosmosData } from '@/drivers/nativeAdapterDriver/useSupplementalCosmosData';
import { useNativeAdapterSession } from '@/drivers/nativeAdapterDriver/useNativeAdapterSession';
import { getSpaceAdapterDescriptorBySlug } from '@/adapters/_@shared/spaceAdapterRegistry';
import type { SpaceAdapterDescriptor } from '@/_contract/types';
import type { ResourceTypeHandle } from '@toolproof-core/genesis/_contract/_index';
import type { CosmosData, SpaceDerivationInput } from '@toolproof-core/visualization/derivation/_contract/derivation';
import type { SpaceSlug } from '@toolproof-core/visualization/_contract/key';
import SpaceRuntime from '@toolproof-core/visualization/runtime/space-runtime';
import type { SpaceOverlayIntent } from '@toolproof-core/visualization/runtime/_contract/overlay';
import { isOverlayAutoplaying, isOverlayIntentEnabled, withOverlayPlaybackControls } from '@toolproof-core/visualization/runtime/_pure/overlayPlayback';
import { getRegisteredSpaceNavigationOptions } from '@/adapters/_@shared/spaceAdapterRegistry';
import {
	SPACE_NAVIGATION_SECTION_ID,
	withSpaceNavigationOverlay,
} from '@/adapters/_@shared/_pure/interaction/spaceOverlay';
import type { RuntimeCallbackCarrier } from '@/drivers/nativeAdapterDriver/_pure/withRuntimeStateChangeCallback';
import { cosmosSpaceUrl, withRouteOrigin } from '@/_pure/cosmosRoute';
import { SPACE_SLUG as EPISODE_SPACE_SLUG } from '@/adapters/episode/spaceIdentity';
import {
	EPISODE_SECTION_ID, RESUME_EPISODE_ACTION,
	KEEP_EXPLORING_ACTION, sessionModeForAction, withSessionOverlay,
} from '@/drivers/nativeAdapterDriver/_pure/sessionOverlay';
import { selectDeveloperMode } from '@/integrations/redux/slices/configSlice';
import { useAppSelector } from '@/integrations/redux/useReduxStore';
import { EpisodeCommentsScene, useEpisodeComments } from './EpisodeComments';
import { PortalPreviewSource, type PortalFocus, type PortalPreview } from './PortalPreviewSource';
import { useCosmosDiagnostics } from '@/CosmosDiagnosticsContext';
import type { AgentChangeset, AgentPlannedFiles } from '@/_contract/agentChat';

const DeveloperPanel = dynamic(() => import('@/drivers/nativeAdapterDriver/DeveloperPanel'));

type ErasedSpaceAdapterDescriptor = SpaceAdapterDescriptor<
	ResourceTypeHandle,
	CosmosData<ResourceTypeHandle>,
	RuntimeCallbackCarrier<unknown>,
	unknown
>;

type NativeAdapterDriverProps = {
	spaceSlug: SpaceSlug;
	episodeHandle?: string;
	arrivalOrigin?: string;
	agentChanges?: AgentChangeset;
	agentPlannedFiles?: AgentPlannedFiles;
};

type NativeAdapterDriverRuntimeHostProps<
	TResourceTypeHandle extends ResourceTypeHandle,
	TCosmosData extends CosmosData<TResourceTypeHandle>,
	TRuntimeState,
	TConfig extends RuntimeCallbackCarrier<TRuntimeState>,
> = {
	descriptor: SpaceAdapterDescriptor<
		TResourceTypeHandle,
		TCosmosData,
		TConfig,
		TRuntimeState
	>;
	cosmosData: CosmosDataWithTimestamps;
	loading: boolean;
	error: Error | null;
	activeSpaceSlug: SpaceSlug;
	episodeHandle?: string;
	arrivalOrigin?: string;
	supplementalCosmosData?: Partial<TCosmosData>;
	agentChanges?: AgentChangeset;
	agentPlannedFiles?: AgentPlannedFiles;
};

function NativeAdapterDriverDataHost<
	TResourceTypeHandle extends ResourceTypeHandle,
	TCosmosData extends CosmosData<TResourceTypeHandle>,
	TRuntimeState,
	TConfig extends RuntimeCallbackCarrier<TRuntimeState>,
>(props: NativeAdapterDriverRuntimeHostProps<TResourceTypeHandle, TCosmosData, TRuntimeState, TConfig>) {
	const { descriptor } = props;
	const result = useSupplementalCosmosData(descriptor);
	if (descriptor.loadSupplementalCosmosData) {
		if (result?.descriptor !== descriptor) return <div role="status">Loading adapter data...</div>;
		if (result.error) return <div role="alert">{result.error.message}</div>;
	}
	return <NativeAdapterDriverRuntimeHost {...props}
		supplementalCosmosData={result?.descriptor === descriptor ? result.data : undefined} />;
}

function NativeAdapterDriverRuntimeHost<
	TResourceTypeHandle extends ResourceTypeHandle,
	TCosmosData extends CosmosData<TResourceTypeHandle>,
	TRuntimeState,
	TConfig extends RuntimeCallbackCarrier<TRuntimeState>,
>(
	props: NativeAdapterDriverRuntimeHostProps<
		TResourceTypeHandle,
		TCosmosData,
		TRuntimeState,
		TConfig
	>,
) {
	const { descriptor, cosmosData, loading, error, activeSpaceSlug, episodeHandle, arrivalOrigin } = props;
	const developerMode = useAppSelector(selectDeveloperMode);
	const diagnostics = useCosmosDiagnostics();
	const [portalFocus, setPortalFocus] = useState<PortalFocus>();
	const [portalPreview, setPortalPreview] = useState<PortalPreview>();
	const deriveSpace = useMemo(
		() => descriptor.baseDescriptor.adapter.createSpaceDeriver(),
		[descriptor],
	);
	const router = useRouter();
	const baseDescriptor = descriptor.baseDescriptor;
	const enrichedCosmosData = useMemo(
		() => ({
			...baseDescriptor.buildCosmosData(cosmosData.resourcesByType, cosmosData.timestampByHandleByType),
			...props.supplementalCosmosData,
		}),
		[baseDescriptor, cosmosData.resourcesByType, cosmosData.timestampByHandleByType, props.supplementalCosmosData],
	);
	const runtimeEffectOwner = useMemo(() => descriptor.createRuntimeEffectOwner?.(), [descriptor]);
	const presentation = descriptor.episodePresentation;
	const session = useNativeAdapterSession({
		descriptor, cosmosData: enrichedCosmosData, loading, episodeHandle, arrivalOrigin,
		episodeSoundDefault: developerMode,
		stopRuntimeEffects: () => runtimeEffectOwner?.dispose?.(),
		navigateToEpisode: (handle) => window.history.pushState(null, '', cosmosSpaceUrl(activeSpaceSlug, handle)),
	});
	const {
		runtimeState, runtimeConfig, runtimeController, derivationState, routeError,
		mode: sessionMode, busy: sessionBusy, episodeAvailable, choosingEpisode, confirmRestore,
		playbackActive, animationSuspended, playbackResetKey, freeCamera, setFreeCamera,
	} = session;
	useEffect(() => {
		if (freeCamera && !developerMode) setFreeCamera(false);
	}, [freeCamera, developerMode, setFreeCamera]);

	const derivationConfig = useMemo(() => {
		const config = descriptor.withAgentChanges?.(runtimeConfig, props.agentChanges) ?? runtimeConfig;
		return descriptor.withAgentPlannedFiles?.(config, props.agentPlannedFiles) ?? config;
	}, [descriptor, runtimeConfig, props.agentChanges, props.agentPlannedFiles]);
	const derivationInput = useMemo<SpaceDerivationInput<TCosmosData, TConfig, TRuntimeState>>(
		() => ({
			spaceKey: baseDescriptor.spaceKey,
			cosmosData: enrichedCosmosData,
			runtimeState: derivationState.runtimeState,
			config: derivationConfig,
		}),
		[enrichedCosmosData, baseDescriptor.spaceKey, derivationConfig, derivationState],
	);
	const derivationOutput = useMemo(
		() => deriveSpace(derivationInput),
		[deriveSpace, derivationInput],
	);

	const adapterOverlayModel = useMemo(
		() => runtimeController.buildOverlayModel?.({
			config: runtimeConfig,
			runtimeState,
			cosmosData: enrichedCosmosData,
			derivationOutput,
		}),
		[runtimeController, runtimeConfig, runtimeState, enrichedCosmosData, derivationOutput],
	);
	const baseOverlayModel = useMemo(
		() => {
			const model = adapterOverlayModel && presentation ? presentation.overlay(adapterOverlayModel,
				sessionMode === 'demonstration') : adapterOverlayModel;
			const audition = process.env.NODE_ENV === 'development' && episodeHandle
				? presentation?.support.auditions?.resolveEpisode(episodeHandle) : undefined;
			const sessionOverlay = presentation ? withSessionOverlay({
				model: model ?? { overlayId: `space-${activeSpaceSlug}`, title: 'Space', sections: [] },
				sessionMode, episodes: [...presentation.episodes,
					...(process.env.NODE_ENV === 'development'
						? presentation.developerEpisodes?.filter(episode => episode.handle === episodeHandle) ?? [] : []),
					...(audition ? [{
						handle: audition.handle, title: audition.title,
					}] : [])], episodeHandle,
				choosingEpisode, confirmRestore,
				busy: sessionBusy, available: episodeAvailable, adapterSectionId: presentation.adapterSectionId,
			}) : model;
			return withSpaceNavigationOverlay({ activeSpaceSlug, overlayModel: sessionOverlay });
		},
		[activeSpaceSlug, adapterOverlayModel, presentation, sessionMode, episodeHandle, confirmRestore,
			choosingEpisode, sessionBusy, episodeAvailable],
	);

	useAdapterRuntimeEffects({
		owner: runtimeEffectOwner, config: runtimeConfig, runtimeState, cosmosData: enrichedCosmosData,
		playbackActive, animationSuspended, loading, routeError,
	});
	const overlayModel = useMemo(
		() => withOverlayPlaybackControls(baseOverlayModel, playbackActive, animationSuspended),
		[baseOverlayModel, playbackActive, animationSuspended]);
	const supportsPause = Boolean(runtimeController.reducePlaybackSuspension);
	// Developers inspect a paused demonstration freely; Play restores the authored camera.
	const cameraFree = sessionMode === 'exploration' ? freeCamera
		: developerMode && supportsPause && animationSuspended;
	const wordTraceSection = overlayModel?.sections.find(section =>
		section.sectionKind === 'select' && section.sectionId === 'episode-subtitle-trace');
	const groupingSection = sessionMode === 'exploration' ? overlayModel?.sections.find(section =>
		section.sectionKind === 'select' && section.sectionId === 'workflow-exploration-grouping') : undefined;
	const commentsContext = sessionMode === 'demonstration' ? presentation?.comments?.(runtimeState) : undefined;
	const comments = useEpisodeComments(commentsContext, activeSpaceSlug, developerMode);
	const displayedOverlayModel = overlayModel ? { ...overlayModel,
		sections: overlayModel.sections.filter(section => section.sectionId !== 'episode-subtitle-trace'
			&& section.sectionId !== 'workflow-exploration-grouping'
			&& (section.sectionKind !== 'diagnostics' || diagnostics.derivation)),
	} : undefined;
	const runtimeTickIntervalMs = loading || routeError || playbackActive || animationSuspended ? undefined : runtimeController.getRuntimeTickIntervalMs?.({
		config: runtimeConfig,
		runtimeState,
		cosmosData: enrichedCosmosData,
	});

	useAdapterRuntimeTicks({
		controller: runtimeController, intervalMs: runtimeTickIntervalMs,
		isSuspended: session.isAnimationSuspended, onTick: session.onRuntimeTick,
	});

	const handleOverlayIntent = (intent: SpaceOverlayIntent) => {
		if (!isOverlayIntentEnabled(overlayModel, intent)) return;
		const leavesEpisode = (intent.overlayIntentKind === 'select-changed'
			&& (intent.sectionId === EPISODE_SECTION_ID || intent.sectionId === SPACE_NAVIGATION_SECTION_ID))
			|| (intent.overlayIntentKind === 'action-triggered'
				&& (sessionModeForAction(intent.actionId) === 'exploration'
					|| !!presentation?.nextEpisode?.(runtimeState, intent)));
		if (comments.dirtyCount && leavesEpisode
			&& !window.confirm('Leave this episode and discard its unsaved comment edits?')) return;
		if (intent.overlayIntentKind === 'action-triggered') {
			const mode = sessionModeForAction(intent.actionId);
			if (mode) { session.selectMode(mode); return; }
		}
		if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === EPISODE_SECTION_ID) {
			if (episodeAvailable && presentation?.episodes.some((episode) => episode.handle === intent.value)) session.navigateEpisode(intent.value, true);
			return;
		}
		if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === 'episode-beat') {
			session.resetPlayback();
		}
		if (intent.overlayIntentKind === 'action-triggered') {
			if (intent.actionId === 'episode-next' || intent.actionId === 'episode-prev') {
				session.resetPlayback();
			}
			if (sessionMode === 'demonstration') {
				const next = presentation?.nextEpisode?.(runtimeState, intent);
				if (next) { session.navigateEpisode(next, true); return; }
			}
			if (intent.actionId === RESUME_EPISODE_ACTION) { session.resumeEpisode(true); return; }
			if (intent.actionId === KEEP_EXPLORING_ACTION) {
				session.keepExploring();
				return;
			}
		}
		if (intent.overlayIntentKind === 'select-changed'
			&& intent.sectionId === SPACE_NAVIGATION_SECTION_ID) {
			const selectedSpace = getRegisteredSpaceNavigationOptions().find(
				(option) => option.spaceSlug === intent.value,
			);
			if (selectedSpace !== undefined && selectedSpace.spaceSlug !== activeSpaceSlug) {
				router.push(cosmosSpaceUrl(selectedSpace.spaceSlug));
			}
			return;
		}
		if (!runtimeController.reduceOverlayIntent) return;
		if (intent.overlayIntentKind === 'select-changed' && intent.sectionId === presentation?.adapterSectionId) {
			const section = baseOverlayModel.sections.find((entry) => entry.sectionId === intent.sectionId);
			if (section?.sectionKind !== 'select' || section.selectedValue === intent.value
				|| !section.options.some((option) => option.value === intent.value)) return;
			session.prepareAdapterSelection();
		}
		if (intent.overlayIntentKind === 'action-triggered') {
			const action = baseOverlayModel.sections.flatMap((section) => section.sectionKind === 'actions' ? section.actions : [])
				.find((entry) => entry.actionId === intent.actionId);
			if (action?.playbackRole === 'toggle' && (playbackActive || animationSuspended || isOverlayAutoplaying(baseOverlayModel))) {
				session.togglePlaybackSuspension();
				return;
			}
			if (action?.playbackRole === 'reset') {
				session.resetPlayback();
			}
		}
		session.reduceAdapterIntent(intent);
	};

	if (loading) return <div role="status">Loading Cosmos data...</div>;
	if (routeError) return <div role="alert">{routeError}</div>;
	return (
		<>
			<NativeAdapterDriverObserver
				spaceKey={baseDescriptor.spaceKey}
				cosmosData={enrichedCosmosData}
				error={error}
				derivationOutput={derivationOutput}
				enabled={diagnostics.derivation}
			/>
			<SpaceRuntime
				derivationOutput={derivationOutput}
				{...(displayedOverlayModel ? { overlayModel: displayedOverlayModel, onOverlayIntent: handleOverlayIntent } : {})}
				{...(runtimeEffectOwner?.spokenProgress ? { readSubtitleProgress: runtimeEffectOwner.spokenProgress } : {})}
				{...(runtimeEffectOwner?.secondsSinceCue ? { readCueElapsed: runtimeEffectOwner.secondsSinceCue } : {})}
				onDerivationTransitionComplete={session.onDerivationTransitionComplete}
				onDerivationTransitionPhaseComplete={session.onDerivationTransitionPhaseComplete}
				onPlaybackActivityChange={session.onPlaybackActivityChange}
				animationSuspended={animationSuspended}
				freeCamera={cameraFree}
				freeCameraMode={sessionMode === 'exploration' ? 'navigation' : 'inspection'}
				{...(sessionMode === 'demonstration' ? { xrPlayer: {
					episodeSectionId: EPISODE_SECTION_ID,
					soundSectionId: 'episode-sound',
					errorSectionId: 'episode-error',
					onPause: () => {
						if (!session.isAnimationSuspended()
							&& (playbackActive || isOverlayAutoplaying(baseOverlayModel))) session.togglePlaybackSuspension();
					},
				} } : {})}
				{...(sessionMode === 'exploration' && episodeAvailable && presentation?.episodes.length ? { xrEpisodePicker: {
					options: presentation.episodes.map((episode) => ({ value: episode.handle, label: episode.title })),
					onChoose: (handle: string) => {
						if (presentation.episodes.some((episode) => episode.handle === handle)) {
							session.navigateEpisode(handle, true);
						}
					},
				} } : {})}
				playbackResetKey={playbackResetKey}
				onPortalEnter={(destination) => router.push(destination)}
				onPortalFocus={(portal) => setPortalFocus(portal
					? { portalKey: portal.portalKey, destination: portal.destination } : undefined)}
				{...(portalPreview ? { portalPreview } : {})}
				{...(episodeHandle && activeSpaceSlug !== EPISODE_SPACE_SLUG
					? { returnPortal: { destination: withRouteOrigin(cosmosSpaceUrl(EPISODE_SPACE_SLUG),
						cosmosSpaceUrl(activeSpaceSlug, episodeHandle)), title: 'All episodes' } } : {})}
				overlayFooter={developerMode
					? <DeveloperPanel derivationOutput={derivationOutput} busy={sessionBusy || animationSuspended}
						comments={commentsContext ? comments : undefined}
						commentsOnly={process.env.NODE_ENV !== 'development'}
						wordTrace={wordTraceSection?.sectionKind === 'select' ? wordTraceSection : undefined}
						grouping={groupingSection?.sectionKind === 'select' ? groupingSection : undefined}
						freeCamera={supportsPause && sessionMode === 'exploration'
							? { checked: freeCamera, onChange: setFreeCamera } : undefined}
						onOverlayIntent={handleOverlayIntent}
						onPreview={session.navigateEpisode}
						episode={sessionMode === 'demonstration' && episodeHandle && presentation ? {
							handle: episodeHandle, getPlan: () => presentation.inspectPlan(runtimeState),
							auditions: process.env.NODE_ENV === 'development' && presentation.support.auditions
								&& (episodeHandle === presentation.support.auditions.source.handle
									|| presentation.support.auditions.isHandle(episodeHandle))
								? presentation.support.auditions : undefined,
							spaceSlug: activeSpaceSlug,
						} : undefined} /> : undefined}
			/>
			{developerMode && commentsContext ? <EpisodeCommentsScene owner={comments} /> : null}
			<PortalPreviewSource focus={portalFocus} onPreview={setPortalPreview} />
		</>
	);
}

export default function NativeAdapterDriver(props: NativeAdapterDriverProps) {
	const { spaceSlug, episodeHandle, arrivalOrigin } = props;
	const descriptor = useMemo(
		() => getSpaceAdapterDescriptorBySlug(spaceSlug),
		[spaceSlug],
	) as ErasedSpaceAdapterDescriptor | undefined;
	const resourceTypeInputSpec = useMemo(() => descriptor?.baseDescriptor.getResourceTypeInputSpec(), [descriptor]);
	if (!descriptor || !resourceTypeInputSpec) {
		return (
			<div style={{ width: '100%', height: '100vh', display: 'grid', placeItems: 'center' }}>
				<div style={{ padding: 16, border: '1px solid #ddd', borderRadius: 8, background: '#fff' }}>
					Unsupported spaceSlug: {spaceSlug}
				</div>
			</div>
		);
	}

	return (
		<CosmosDataProvider resourceTypeInputSpec={resourceTypeInputSpec}>
			{({ cosmosData, loading, error }) => (
				<NativeAdapterDriverDataHost
					descriptor={descriptor}
					cosmosData={cosmosData}
					loading={loading}
					error={error}
					activeSpaceSlug={spaceSlug}
					episodeHandle={episodeHandle}
					arrivalOrigin={arrivalOrigin}
					agentChanges={props.agentChanges}
					agentPlannedFiles={props.agentPlannedFiles}
				/>
			)}
		</CosmosDataProvider>
	);
}
